import clsx from 'clsx';
import React, {useEffect, useRef, useState} from 'react';
import {Stack, styled} from '@mui/material';
import MuiAvatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import {
  Body,
  Icon,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import {AssetMessageThread} from '@/api/messages/types';
import MessageListItem from './MessageListItem';
import styles from './AssetThreadCard.module.scss';

interface Props {
  thread: AssetMessageThread;
  onOpen: (thread: AssetMessageThread) => void;
}

const ThreadBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: designSystemToken('semantic.fg.brand'),
    color: designSystemToken('semantic.fg.white'),
    fontSize: 10,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
  },
}));

const ThumbnailAvatar = styled(MuiAvatar)({
  width: 32,
  height: 32,
  borderRadius: '4px',
});

const PlaceholderAvatar = styled(MuiAvatar)(() => ({
  width: 32,
  height: 32,
  borderRadius: '4px',
  backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
}));

const STATUS_LABELS: Record<string, string> = {
  processing: 'Processing',
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  archived: 'Archived',
};

function contentTypeLabel(contentType: string): string {
  if (contentType === 'mixed_media') return 'Mixed media';
  return contentType.charAt(0).toUpperCase() + contentType.slice(1);
}

export default function AssetThreadCard({thread, onOpen}: Props) {
  const [isExpanded, setIsExpanded] = useState(thread.hasUnread);
  const wasUnreadRef = useRef(thread.hasUnread);
  const typeLabel = contentTypeLabel(thread.contentType);

  useEffect(() => {
    if (!wasUnreadRef.current && thread.hasUnread) setIsExpanded(true);
    wasUnreadRef.current = thread.hasUnread;
  }, [thread.hasUnread]);

  const handleToggleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsExpanded((value) => !value);
  };

  return (
    <Stack className={styles.card} data-testid="asset-thread-card">
      <Stack direction="row" alignItems="stretch" className={styles.header}>
        <Stack
          component="button"
          type="button"
          direction="row"
          alignItems="flex-start"
          spacing={1.5}
          className={styles.headerLink}
          onClick={() => onOpen(thread)}
          aria-label={`Open ${typeLabel} upload, ${thread.messageCount} ${
            thread.messageCount === 1 ? 'message' : 'messages'
          }${thread.hasUnread ? ', unread' : ''}`}
        >
          <ThreadBadge
            badgeContent={thread.hasUnread ? thread.messageCount : 0}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          >
            {thread.thumbnailUrl ? (
              <ThumbnailAvatar
                src={thread.thumbnailUrl}
                variant="rounded"
                alt="Asset thumbnail"
              />
            ) : (
              <PlaceholderAvatar variant="rounded">
                <Icon
                  name="Navigation-Photos-Options"
                  appearance="neutral"
                  size="small"
                />
              </PlaceholderAvatar>
            )}
          </ThreadBadge>

          <Stack spacing={0.25} className={styles.headerInfo}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              className={styles.titleRow}
            >
              <Label size="md">{typeLabel}</Label>
              {thread.campaignName ? (
                <Stack
                  component="span"
                  direction="row"
                  alignItems="center"
                  className={styles.campaignBadge}
                >
                  <Body size="sm" className={styles.campaignBadgeText}>
                    {thread.campaignName}
                  </Body>
                </Stack>
              ) : null}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              flexWrap="nowrap"
              className={styles.metaRow}
            >
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={styles.metaItem}
              >
                v{thread.versionNumber}
              </Body>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={clsx(styles.metaItem, styles.metaItemTruncate)}
              >
                {STATUS_LABELS[thread.status] ?? thread.status}
              </Body>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={styles.metaItem}
              >
                {thread.messageCount}{' '}
                {thread.messageCount === 1 ? 'message' : 'messages'}
              </Body>
            </Stack>
          </Stack>
        </Stack>

        <Stack
          component="button"
          type="button"
          direction="row"
          alignItems="center"
          justifyContent="center"
          className={styles.toggleButton}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${typeLabel} v${
            thread.versionNumber
          } messages`}
          aria-expanded={isExpanded}
          onClick={handleToggleClick}
        >
          <Icon
            name={
              isExpanded
                ? 'Actions-Arrow-Down-Small'
                : 'Actions-Arrow-Forward-Small'
            }
            appearance="neutral"
            size="small"
          />
        </Stack>
      </Stack>

      {isExpanded && (
        <Stack spacing={1} className={styles.messagesContainer}>
          {thread.messages.map((message) => (
            <MessageListItem key={message.id} message={message} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
